import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { driveAdoptPmDocuments } from '/imports/api/drive';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { toastOnError } from '/imports/ui/utils';
import { DevToolSection } from '/imports/ui/DevToolSection';

/** Dev tool: bring Project Management documents filed before the Documents module into it. Nothing moves
 *  on disk — the drive rows point at the bytes where they already are — so a dry run is cheap and the
 *  real run is undone by deleting the rows it wrote. Idempotent and safe to repeat in batches. */
export const DevToolAdoptPmDocuments = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const run = toastOnError(async (dryRun: boolean) => {
    setLoading(true);
    setResult('');
    try {
      const counts = await driveAdoptPmDocuments.call({ dryRun, limit: 500 });
      setResult(JSON.stringify(counts, null, 2));
      toast.success(`${dryRun ? 'Would link' : 'Linked'} ${counts.versions} version(s) across ${counts.documents} document(s); ${counts.remaining} left`);
    }
    finally {
      setLoading(false);
    }
  });

  return (
    <DevToolSection
      title="Adopt PM Documents"
      description="Give every Project Management document a home in <b>Documents</b>: a folder per project under <b>/Projects</b>, one drive file per document, one drive version per upload. <b>Nothing is moved or copied</b> — the drive rows point at the bytes where they already are, and the PM rows are untouched apart from gaining the two ids that link them. Idempotent, 500 versions at a time. Run the dry run first."
      onRun={() => run(true)}
      buttonText={loading ? strings.Loading() : 'Dry run'}
      loading={loading}
    >
      <Button className="secondary" disabled={loading} onClick={() => run(false)}>{strings.Run()}</Button>
      {result && <pre>{result}</pre> || null}
    </DevToolSection>
  );
};
